import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  FinanceInsightsSchema,
  FinanceSummarySchema,
  type FinanceDateRangeParams,
  type FinanceInsights,
  type FinanceSummary,
} from "@/api/types/finance";

/** Query-key factory — mirrors the dashboard hook's convention. */
export const financeKeys = {
  all: ["finance"] as const,
  summary: (params: FinanceDateRangeParams) => [...financeKeys.all, "summary", params] as const,
  insights: (params: FinanceDateRangeParams) => [...financeKeys.all, "insights", params] as const,
};

function buildRangeParams({ from, to }: FinanceDateRangeParams): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (from) searchParams.set("from", from);
  if (to) searchParams.set("to", to);
  return searchParams;
}

/**
 * Cash Flow tab's data source — GET /v2/finance/summary?from=&to=. Revenue,
 * expenses, net, category breakdown, recurring subscriptions, the 6-month
 * series, and top vendors all come pre-computed; this hook only fetches
 * and validates.
 */
export function useFinanceSummary(params: FinanceDateRangeParams = {}): UseQueryResult<FinanceSummary> {
  return useQuery<FinanceSummary>({
    queryKey: financeKeys.summary(params),
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/v2/finance/summary?${buildRangeParams(params).toString()}`,
      );
      return validateResponse(FinanceSummarySchema, data, "GET /v2/finance/summary");
    },
    staleTime: 15 * 1000,
  });
}

/**
 * Insights tab's data source — GET /v2/finance/insights?from=&to=. Wraps the
 * same summary shape plus a narrative string and its source (`haiku` when an
 * LLM wrote it, `deterministic` when it's template-generated — the UI tags
 * the narrative accordingly). `refetch` backs the "Refresh analysis" button;
 * this is deliberately not on a short staleTime since re-running it may hit
 * a paid model.
 */
export function useFinanceInsights(params: FinanceDateRangeParams = {}): UseQueryResult<FinanceInsights> {
  return useQuery<FinanceInsights>({
    queryKey: financeKeys.insights(params),
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/v2/finance/insights?${buildRangeParams(params).toString()}`,
      );
      return validateResponse(FinanceInsightsSchema, data, "GET /v2/finance/insights");
    },
    staleTime: 60 * 1000,
  });
}
